"use client";

import { forwardRef, useEffect, useRef, useState } from "react";
import helpPaths from "@/shared/ui/icons/svg-o97u2f18ak";
import { SectionBlock } from "@/components/blocks/SectionBlock";

/*
 * Анимация хиро отрабатывает ОДИН РАЗ за браузерную сессию (вкладку).
 * Флаг хранится в sessionStorage — умирает вместе со вкладкой,
 * не течёт в localStorage и не требует таймеров.
 *
 * Три состояния рендера:
 *   idle      — компонент ещё не появился в viewport   → всё скрыто
 *   animating — первый показ, анимация идёт             → CSS animations
 *   done      — sessionStorage уже помечен              → финальное состояние без анимаций
 */

const SESSION_KEY = "practicum_hero_intro_v1";

type AnimState = "idle" | "animating" | "done";

// ── Константы тайминга ─────────────────────────────────────
const STEP = 0.13;           // 130ms на символ
const WORD_PAUSE = 0.25;     // пауза перед "help"
const HELP_START = 9 * STEP + WORD_PAUSE;
const STAR_DELAY = HELP_START + 4 * STEP + 0.25; // после последней буквы
const HEADING_DELAY = STAR_DELAY + 0.65;          // после bounce звёздочки

// ── Порядок символов ───────────────────────────────────────
interface CharPath {
  d: string;
  delay: number;
  clipRule?: "evenodd" | "nonzero";
  fillRule?: "evenodd" | "nonzero";
}

const LOGO_CHARS: CharPath[] = [
  { d: helpPaths.p285c6180, delay: 0 * STEP },
  { d: helpPaths.p6b17300,  delay: 1 * STEP },
  { d: helpPaths.p2e4eba00, delay: 2 * STEP, clipRule: "evenodd", fillRule: "evenodd" },
  { d: helpPaths.p2f9b8100, delay: 3 * STEP },
  { d: helpPaths.p16f88f00, delay: 4 * STEP },
  { d: helpPaths.pd621100,  delay: 5 * STEP },
  { d: helpPaths.pb961400,  delay: 6 * STEP },
  { d: helpPaths.p25fb9700, delay: 7 * STEP },
  { d: helpPaths.p14689480, delay: 8 * STEP },
  { d: helpPaths.p3b8c4700, delay: HELP_START },
  { d: helpPaths.p320af300, delay: HELP_START + 1 * STEP },
  { d: helpPaths.p282e7b00, delay: HELP_START + 2 * STEP },
  { d: helpPaths.p36671240, delay: HELP_START + 3 * STEP },
];

// ── Стили символов в зависимости от состояния ─────────────
function charStyle(delay: number, state: AnimState): React.CSSProperties {
  if (state === "done") return { opacity: 1 };
  if (state === "idle") return { opacity: 0 };
  // animating: base opacity:0 + step-start animation → мгновенный snap в момент delay
  return {
    opacity: 0,
    animation: `char-appear 0.001s step-start ${delay.toFixed(3)}s forwards`,
  };
}

// ── SVG логотипа ───────────────────────────────────────────
function AnimatedHeroLogo({ state }: { state: AnimState }) {
  const starBase: React.CSSProperties = {
    transformBox: "fill-box",
    transformOrigin: "center",
  };

  const starStyle: React.CSSProperties =
    state === "done"
      ? { ...starBase, opacity: 1, transform: "scale(1)" }
      : state === "animating"
        ? {
            ...starBase,
            opacity: 0,
            animation: `star-bounce-zoom 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) ${STAR_DELAY.toFixed(3)}s forwards`,
          }
        : { ...starBase, opacity: 0, transform: "scale(0)" };

  return (
    <svg
      viewBox="0 0 282.876 60.358"
      fill="none"
      className="w-full h-full max-w-[min(800px,90vw)]"
      aria-label="Practicum Help"
    >
      {LOGO_CHARS.map((char, i) => (
        <path
          key={i}
          d={char.d}
          fill="currentColor"
          clipRule={char.clipRule}
          fillRule={char.fillRule}
          style={charStyle(char.delay, state)}
        />
      ))}

      <g style={starStyle}>
        <path
          d={helpPaths.p1d5a8d00}
          fill="#069B93"
          clipRule="evenodd"
          fillRule="evenodd"
        />
      </g>
    </svg>
  );
}

// ── HeroSection ────────────────────────────────────────────
export const HeroSection = forwardRef<HTMLElement>((props, ref) => {
  const [animState, setAnimState] = useState<AnimState>("idle");
  const innerRef = useRef<HTMLElement | null>(null);

  const setRefs = (el: HTMLElement | null) => {
    innerRef.current = el;
    if (typeof ref === "function") ref(el);
    else if (ref) (ref as React.MutableRefObject<HTMLElement | null>).current = el;
  };

  useEffect(() => {
    // Проверяем sessionStorage — флаг живёт ровно одну вкладку-сессию
    const alreadyShown =
      typeof sessionStorage !== "undefined" &&
      sessionStorage.getItem(SESSION_KEY) === "1";

    if (alreadyShown) {
      // Сессия была — показываем финальное состояние без анимации
      setAnimState("done");
      return;
    }

    // Первый показ — ждём попадания в viewport
    const el = innerRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          sessionStorage.setItem(SESSION_KEY, "1");
          setAnimState("animating");
          io.disconnect();
        }
      },
      { threshold: 0.1 },
    );

    io.observe(el);
    return () => io.disconnect();
  }, []);

  const headingBlockStyle: React.CSSProperties =
    animState === "done"
      ? { transform: "translateY(0)" }
      : animState === "animating"
        ? {
            animation: `slide-from-bottom-pure 0.9s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${HEADING_DELAY.toFixed(3)}s both`,
          }
        : { transform: "translateY(105%)" };

  return (
    <section
      ref={setRefs}
      className="flex flex-col"
      style={{
        minHeight:
          "clamp(480px, calc(100svh - 2 * (var(--ring-nav-height, 80px) + var(--ring-nav-padding, 12px) * 2)), 100svh)",
      }}
    >
      <SectionBlock className="flex flex-col gap-fluid-lg h-full p-fluid-xl flex-1">
        {/* Логотип */}
        <div className="flex items-center justify-center flex-1 min-h-0">
          <AnimatedHeroLogo state={animState} />
        </div>

        {/* overflow-hidden скрывает блок до момента выдвижения */}
        <div className="overflow-hidden shrink-0">
          <div
            style={headingBlockStyle}
            className="text-left pt-fluid-lg border-t border-[var(--help)]/20"
          >
            <h1 className="font-content font-semibold text-fluid-4xl leading-tight text-foreground block select-none">
              Центр <span className="text-help">доказательной психологии</span>
            </h1>
            <p className="hidden font-content font-light text-fluid-xl leading-snug text-[var(--foreground)] mt-fluid-sm block select-none max-w-[65ch]">
              Не просто снижение симптомов — восстановление функциональности,
              устойчивости и способности жить полноценно.
            </p>
          </div>
        </div>
      </SectionBlock>
    </section>
  );
});

HeroSection.displayName = "HeroSection";
